import { appLocale } from "../../locales/display.ts";
import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { useCallback, useEffect, useState } from "react";
import { LockKeyhole, RotateCw } from "lucide-react";
import styled from "styled-components";
import { GD } from "src/GD";
import { AccountingCard as Card, KpiLabel, ReserveRefreshButton } from "./styled";
import { formatReserveAsOf, formatReserveAmount, parseRollingReserves, parseRollingReserveLifetime, type RollingReserveCurrency, type RollingReserveLifetimeCurrency } from "./rollingReserveData";
import { RollingReserveTransactionsPopup } from "./RollingReserveTransactionsPopup";
import { RollingReserveLifetimeCard } from "./RollingReserveLifetimeCard";

const CurrencyMetrics = styled.dl`
	display: grid;
	flex: 1;
	grid-template-columns: repeat(auto-fit, minmax(min(100%, 220px), 1fr));
	gap: 12px;
	margin: 16px 0 0;
`;

const ReserveMetric = styled.div`
	position: relative;
	min-width: 0;
	padding: 12px 14px;
	border-radius: 10px;
	background: #f5f3ff;
	dt { color: #667085; font-size: 12px; line-height: 18px; margin-bottom: 6px; }
	dd { margin: 0; font-size: 26px; font-weight: 600; color: #1a1d26; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
`;

const ReserveAction = styled.button`
	position: absolute;
	inset: 0;
	width: 100%;
	border: 0;
	border-radius: inherit;
	background: transparent;
	cursor: pointer;
	&:hover { background: rgba(109, 92, 232, 0.06); }
	&:focus-visible { outline: 2px solid #6d5ce8; outline-offset: 2px; }
`;

// The held and lifetime cards share one response and refresh together.
export const RollingReserveHeldCards = ({ clientId, projectId, userId, channelFiltered = false }: { clientId: number; projectId: number; userId: number; channelFiltered?: boolean }) => {
	useLocale();
	const [attempt, setAttempt] = useState(0);
	const [selectedCurrency, setSelectedCurrency] = useState<string>();
	const closeTransactions = useCallback(() => setSelectedCurrency(undefined), []);
	const [state, setState] = useState<{ busy: boolean; rows?: RollingReserveCurrency[]; lifetime?: RollingReserveLifetimeCurrency[] | null; lifetimeError?: string; asOf?: string; error?: string }>({ busy: true });
	useEffect(() => {
		let cancelled = false;
		const subscription = GD.S_AUTH_LOG_OUT.subscribe(() => { cancelled = true; setState({ busy: false }); });
		setState({ busy: true });
		const load = async () => {
			const checkContext = async () => {
				const [profile, project] = await Promise.all([GD.REQ_PROFILE.request(), GD.REQ_CURRENT_PROJECT.request()]);
				if (cancelled || profile?.id !== userId || project?.id !== projectId) throw new Error("Context changed");
			};
			try {
				await checkContext();
				// Current held balance is independent of the overview period and channel filter.
				const response = await GD.REQ_API.request({
					method: "rollings/held", apiVersion: 2, expectedProjectId: projectId,
					headers: { ignoreBusy: "true" }, data: { client_id: clientId },
				});
				await checkContext();
				if (response?.error) throw new Error(msg("reserveRequestFailed"));
				const rows = parseRollingReserves(response?.data, clientId);
				let lifetime: RollingReserveLifetimeCurrency[] | null = null;
				let lifetimeError: string | undefined;
				try { lifetime = parseRollingReserveLifetime(response.data, clientId); }
				catch { lifetimeError = msg("couldNotReadLifetimeRollingReserves"); }
				if (!cancelled) setState({ busy: false, rows, lifetime, lifetimeError, asOf: formatReserveAsOf(response.data?.as_of) });
			} catch {
				if (!cancelled) setState({ busy: false, error: msg("couldNotLoadRollingReserves") });
			}
		};
		void load();
		return () => { cancelled = true; GD.S_AUTH_LOG_OUT.unsubscribe(subscription); };
	}, [clientId, projectId, userId, attempt]);

	return <><Card style={{ height: "auto", minWidth: 0 }} role="region" aria-label={msg("rollingReserve_c2352be7")} aria-busy={state.busy}>
		<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
			<div style={{ display: "flex", alignItems: "center", gap: 12 }}>
				<div style={{ padding: 10, borderRadius: 12, color: "#6d5ce8", background: "#f1effd", display: "flex" }}><LockKeyhole size={20} /></div>
				<div>
					<div style={{ fontSize: 14, fontWeight: 600, color: "#1a1d26" }}>{msg("rollingReserveCurrentlyHeld")}</div>
					<KpiLabel>{state.asOf ? msg("templateAsOf_4096bd2d", { v0: state.asOf }) : msg("currentlyHeld")}</KpiLabel>
					{channelFiltered && <KpiLabel style={{ display: "block" }}>{msg("reserveCoversAllChannels")}</KpiLabel>}
				</div>
			</div>
			<ReserveRefreshButton $busy={state.busy} title={state.error ? msg("retry") : msg("refresh")} aria-label={state.error ? msg("retry") : msg("refresh")} disabled={state.busy} onClick={() => setAttempt(value => value + 1)}><RotateCw size={16} aria-hidden="true" /></ReserveRefreshButton>
		</div>
		<div role="status" aria-live="polite">
			{state.busy && <KpiLabel style={{ display: "block", marginTop: 12 }}>{msg("loadingReserves")}</KpiLabel>}
			{state.error && <div style={{ marginTop: 12, color: "#b42332", fontSize: 13 }}>{state.error}</div>}
			{state.rows?.length === 0 && <KpiLabel style={{ display: "block", marginTop: 12 }}>{msg("noReservesCurrentlyHeld")}</KpiLabel>}
		</div>
		{!!state.rows?.length && <CurrencyMetrics>
			{state.rows.map(row => <ReserveMetric key={row.currency} aria-label={msg("templateReserve_6156d3d2", { v0: row.currency })}>
				<dt>{row.currency}</dt>
				<dd>
					{formatReserveAmount(row.held)}
					{(row.reserves !== undefined || row.nextRelease) && <div style={{ marginTop: 8, color: "#667085", fontSize: 12, fontWeight: 400, lineHeight: 1.7 }}>
						{row.reserves !== undefined && <div>{msg("heldReservesCount", { count: row.reserves, formatted: row.reserves.toLocaleString(appLocale("en-GB")) })}</div>}
						{row.nextRelease && <div>{msg("nextRelease")}<span style={{ whiteSpace: "nowrap" }}>{row.nextRelease}</span></div>}
					</div>}
					<div style={{ marginTop: 8, color: "#6d5ce8", fontSize: 12, fontWeight: 500 }}>{msg("viewTransactions")}</div>
					<ReserveAction type="button" aria-label={msg("templateViewHeldTransactionsIn_bdaf7dc5", { v0: row.currency })} aria-haspopup="dialog" onClick={() => setSelectedCurrency(row.currency)} />
				</dd>
			</ReserveMetric>)}
		</CurrencyMetrics>}
		{selectedCurrency && <RollingReserveTransactionsPopup key={`${clientId}:${projectId}:${userId}:${selectedCurrency}`} clientId={clientId} currency={selectedCurrency} projectId={projectId} userId={userId} onClose={closeTransactions} />}
	</Card>
		<RollingReserveLifetimeCard busy={state.busy} rows={state.lifetime} error={state.error ?? state.lifetimeError} asOf={state.asOf} channelFiltered={channelFiltered} onRefresh={() => setAttempt(value => value + 1)} />
	</>;
};
